/**
 * Configuration Flow State Management Hook
 * Manages questions, customizations, and ordering in React state only
 * Always uses fresh generated data from products/categories/options
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { IFlowQuestion, generateJsonFlow } from '../utils/jsonFlowGenerator';
import { IProduct } from '../types/productSetup';
import { ICategory } from '../types/categorySetup';
import { IConfigOption } from '../types/configOption';

export interface QuestionCustomization {
  questionText?: string;
  helperText?: string;
  isHidden?: boolean;
}

interface UseChatbotFlowProps {
  products: IProduct[];
  categories: ICategory[];
  options: IConfigOption[];
  remoteFlow?: IFlowQuestion[];
}

interface UseChatbotFlowReturn {
  questions: IFlowQuestion[];
  customizations: Record<string, QuestionCustomization>;
  questionOrder: string[];
  optionOrders: Record<string, string[]>; // questionId -> array of optionIds in order
  hasUnsavedChanges: boolean;
  isLoading: boolean;
  updateCustomization: (
    questionId: string,
    customization: Partial<QuestionCustomization>
  ) => void;
  updateQuestionOrder: (newOrder: string[]) => void;
  updateOptionOrder: (questionId: string, newOrder: string[]) => void;
  resetCustomizations: () => void;
  getQuestionWithCustomization: (
    question: IFlowQuestion
  ) => IFlowQuestion & { helperText?: string };
}

export const useChatbotFlow = ({
  products,
  categories,
  options,
  remoteFlow,
}: UseChatbotFlowProps): UseChatbotFlowReturn => {
  const [customizations, setCustomizations] = useState<
    Record<string, QuestionCustomization>
  >({});
  const [questionOrder, setQuestionOrder] = useState<string[]>([]);
  const [optionOrders, setOptionOrders] = useState<Record<string, string[]>>({});
  const [isLoading, setIsLoading] = useState(true);

  // Generate fresh questions from current data (sequential IDs, not yet stable)
  const rawGeneratedQuestions = useMemo(() => {
    if (categories.length === 0) return [];
    const flow = generateJsonFlow(categories, options, products);
    return flow.questionFlow;
  }, [products, categories, options]);

  // Strip the first occurrence of "_cond" from a responsePath input suffix.
  // Needed because the multiOptionGroups change folded conditional options back
  // into base questions, dropping the "_cond" suffix from newly generated paths
  // while remote-saved configs still carry it.
  const normalizeSuffix = useCallback(
    (suffix: string) => suffix.replace('_cond', ''),
    []
  );

  // Re-assign stable questionIds:
  // - Existing questions (matched by responsePath against remote) keep their remote ID
  // - Brand-new questions get an ID appended after the highest remote question number
  const generatedQuestions = useMemo(() => {
    if (!remoteFlow || remoteFlow.length === 0) return rawGeneratedQuestions;

    // Build three lookups from the saved remote flow (each progressively looser):
    // 1. Exact responsePath → questionId
    // 2. (category, optionType, inputSuffix) → questionId  — handles the string→numeric
    //    responsePath format change (e.g. "...deep_bottom_rails.5" → "...2.5")
    // 3. (category, optionType, normalizedSuffix) → questionId  — also handles the
    //    _cond suffix being dropped (e.g. "5_cond" → "5")
    const remoteIdByPath = new Map<string, string>();
    const remoteIdByTuple = new Map<string, string>();
    const remoteIdByNormTuple = new Map<string, string>();
    let maxRemoteNum = 0;

    remoteFlow.forEach((q) => {
      const key = q.responsePath || `${q.questionId}_${q.category}_${q.optionType}`;
      remoteIdByPath.set(key, q.questionId);

      if (q.responsePath) {
        const inputSuffix = q.responsePath.split('.').slice(2).join('.');
        const tupleKey = `${q.category}__${q.optionType ?? ''}__${inputSuffix}`;
        if (!remoteIdByTuple.has(tupleKey)) remoteIdByTuple.set(tupleKey, q.questionId);

        const normKey = `${q.category}__${q.optionType ?? ''}__${normalizeSuffix(inputSuffix)}`;
        if (!remoteIdByNormTuple.has(normKey)) remoteIdByNormTuple.set(normKey, q.questionId);
      }

      const num = parseInt(q.questionId.replace(/\D/g, ''), 10);
      if (!isNaN(num)) maxRemoteNum = Math.max(maxRemoteNum, num);
    });

    // Track consumed remote IDs so two local questions don't claim the same one
    const usedRemoteIds = new Set<string>();
    let newIdCounter = maxRemoteNum + 1;

    return rawGeneratedQuestions.map((q) => {
      // 1. Exact responsePath match (works when local + remote share the same format)
      const key = q.responsePath || `${q.questionId}_${q.category}_${q.optionType}`;
      const exactMatch = remoteIdByPath.get(key);
      if (exactMatch && !usedRemoteIds.has(exactMatch)) {
        usedRemoteIds.add(exactMatch);
        return { ...q, questionId: exactMatch };
      }

      if (q.responsePath) {
        const inputSuffix = q.responsePath.split('.').slice(2).join('.');

        // 2. Exact tuple — handles string→numeric responsePath format change
        const tupleKey = `${q.category}__${q.optionType ?? ''}__${inputSuffix}`;
        const tupleMatch = remoteIdByTuple.get(tupleKey);
        if (tupleMatch && !usedRemoteIds.has(tupleMatch)) {
          usedRemoteIds.add(tupleMatch);
          return { ...q, questionId: tupleMatch };
        }

        // 3. Normalized tuple — also handles _cond suffix being stripped
        const normKey = `${q.category}__${q.optionType ?? ''}__${normalizeSuffix(inputSuffix)}`;
        const normMatch = remoteIdByNormTuple.get(normKey);
        if (normMatch && !usedRemoteIds.has(normMatch)) {
          usedRemoteIds.add(normMatch);
          return { ...q, questionId: normMatch };
        }
      }

      // 4. Truly new question — assign ID from after the last remote question
      return { ...q, questionId: `Q${String(newIdCounter++).padStart(4, '0')}` };
    });
  }, [rawGeneratedQuestions, remoteFlow, normalizeSuffix]);

  // Helper function to merge options: preserve remote order, use fresh local data
  const mergeQuestionOptions = useCallback(
    (
      remoteQuestion: IFlowQuestion,
      localQuestion: IFlowQuestion
    ): IFlowQuestion => {
      // Create a map of local options by optionId for quick lookup
      const localOptionsMap = new Map(
        localQuestion.options.map((opt) => [opt.optionId, opt])
      );

      const mergedOptions: typeof localQuestion.options = [];

      // Sort remote options by order field (if exists) or by their array position
      const sortedRemoteOptions = [...remoteQuestion.options].sort((a, b) => {
        const orderA = a.order ?? remoteQuestion.options.indexOf(a) + 1;
        const orderB = b.order ?? remoteQuestion.options.indexOf(b) + 1;
        return orderA - orderB;
      });

      // Preserve remote option order, but use fresh local data
      sortedRemoteOptions.forEach((remoteOpt) => {
        const localOpt = localOptionsMap.get(remoteOpt.optionId);
        if (localOpt) {
          // Option exists in both: use fresh local data with remote order/responseValue
          // Prefer local thumbnail (up-to-date from Dataverse), fall back to remote if local is missing
          // Preserve remote responseValue if set — it may have been manually configured
          mergedOptions.push({
            ...localOpt,
            thumbnail: localOpt.thumbnail ?? remoteOpt.thumbnail,
            order: remoteOpt.order,
            responseValue: remoteOpt.responseValue ?? localOpt.responseValue,
          });
          localOptionsMap.delete(remoteOpt.optionId);
        }
      });

      // Append any new options (from local generation) at the end
      localOptionsMap.forEach((localOpt) => {
        mergedOptions.push({
          ...localOpt,
          order: mergedOptions.length + 1,
        });
      });

      // Reassign order sequentially
      const finalOptions = mergedOptions.map((opt, index) => ({
        ...opt,
        order: index + 1,
      }));

      // If the user previously customised the question text (isCustomModified flag
      // saved to the backend), preserve that text. Otherwise use the freshly
      // generated local text so structural generation changes are picked up.
      const useRemoteText = remoteQuestion.isCustomModified === true;
      return {
        ...localQuestion,
        // Preserve the remote responsePath once it has been saved — regenerating
        // questions after an option label change must not override an established path.
        responsePath: remoteQuestion.responsePath || localQuestion.responsePath,
        question: useRemoteText ? remoteQuestion.question : localQuestion.question,
        isCustomModified: useRemoteText,
        options: finalOptions,
      };
    },
    []
  );

  // Helper function to apply custom option order to a question
  const applyCustomOptionOrder = useCallback(
    (question: IFlowQuestion): IFlowQuestion => {
      const customOrder = optionOrders[question.questionId];
      if (!customOrder || customOrder.length === 0) {
        return question;
      }

      const optionsMap = new Map(
        question.options.map((opt) => [opt.optionId, opt])
      );

      const orderedOptions: typeof question.options = [];

      // Apply custom order
      customOrder.forEach((optionId) => {
        const option = optionsMap.get(optionId);
        if (option) {
          orderedOptions.push(option);
          optionsMap.delete(optionId);
        }
      });

      // Add any new options that weren't in the custom order
      optionsMap.forEach((option) => {
        orderedOptions.push(option);
      });

      // Reassign order field
      const finalOptions = orderedOptions.map((opt, index) => ({
        ...opt,
        order: index + 1,
      }));

      return {
        ...question,
        options: finalOptions,
      };
    },
    [optionOrders]
  );

  // Get ordered questions - always merge local generated questions into remote order
  const questions = useMemo(() => {
    const questionsMap = new Map(
      generatedQuestions.map((q) => [q.questionId, q])
    );

    // Priority 1: If user has manually reordered (questionOrder exists), use that
    if (questionOrder.length > 0) {
      const orderedQuestions: IFlowQuestion[] = [];

      // Create a map of remote questions for merging (by responsePath for stable lookup)
      const remoteQuestionsMap = remoteFlow && remoteFlow.length > 0
        ? new Map(remoteFlow.map((q) => [q.questionId, q]))
        : null;

      questionOrder.forEach((id) => {
        const generatedQ = questionsMap.get(id);
        if (generatedQ) {
          // If we have remote flow, merge to preserve remote option order and question text
          const remoteQ = remoteQuestionsMap?.get(id);
          const question = remoteQ
            ? mergeQuestionOptions(remoteQ, generatedQ)
            : generatedQ;

          orderedQuestions.push(question);
          questionsMap.delete(id);
        }
      });

      // Add any new questions that weren't in the order
      questionsMap.forEach((question) => {
        orderedQuestions.push(question);
      });

      return orderedQuestions.map((q, index) => {
        const questionWithCustomOptions = applyCustomOptionOrder(q);
        return {
          ...questionWithCustomOptions,
          step: index + 1,
        };
      });
    }

    // Priority 2: If we have remote flow, merge local generated into remote order
    if (remoteFlow && remoteFlow.length > 0) {
      const orderedQuestions: IFlowQuestion[] = [];

      // Sort remote by step to preserve saved order
      const sortedRemote = [...remoteFlow].sort((a, b) => (a.step ?? 0) - (b.step ?? 0));

      // Lookup 1: exact responsePath → generated question
      const generatedByPath = new Map(
        generatedQuestions
          .filter((q) => q.responsePath)
          .map((q) => [q.responsePath, q])
      );

      // Lookup 3: exact (category + optionType + inputSuffix) → generated question
      // Handles string→numeric responsePath format change.
      const generatedByTuple = new Map<string, IFlowQuestion>();
      generatedQuestions.forEach((q) => {
        if (!q.responsePath) return;
        const inputSuffix = q.responsePath.split('.').slice(2).join('.');
        const key = `${q.category}__${q.optionType ?? ''}__${inputSuffix}`;
        if (!generatedByTuple.has(key)) generatedByTuple.set(key, q);
      });

      // Lookup 4: normalized (category + optionType + suffix-without-_cond) → generated question
      // Handles _cond suffix being dropped from locally generated paths.
      const generatedByNormTuple = new Map<string, IFlowQuestion>();
      generatedQuestions.forEach((q) => {
        if (!q.responsePath) return;
        const inputSuffix = q.responsePath.split('.').slice(2).join('.');
        const key = `${q.category}__${q.optionType ?? ''}__${normalizeSuffix(inputSuffix)}`;
        if (!generatedByNormTuple.has(key)) generatedByNormTuple.set(key, q);
      });

      const removeFromAllMaps = (q: IFlowQuestion) => {
        if (q.responsePath) {
          generatedByPath.delete(q.responsePath);
          const inputSuffix = q.responsePath.split('.').slice(2).join('.');
          generatedByTuple.delete(`${q.category}__${q.optionType ?? ''}__${inputSuffix}`);
          generatedByNormTuple.delete(`${q.category}__${q.optionType ?? ''}__${normalizeSuffix(inputSuffix)}`);
        }
        questionsMap.delete(q.questionId);
      };

      sortedRemote.forEach((remoteQ) => {
        // 1. Exact responsePath match
        // 2. questionId match (works when stable ID assignment succeeded)
        let generatedQ: IFlowQuestion | undefined =
          (remoteQ.responsePath && generatedByPath.get(remoteQ.responsePath)) ||
          questionsMap.get(remoteQ.questionId);

        if (!generatedQ && remoteQ.responsePath) {
          const inputSuffix = remoteQ.responsePath.split('.').slice(2).join('.');

          // 3. Exact tuple — handles string→numeric responsePath format change
          const tupleKey = `${remoteQ.category}__${remoteQ.optionType ?? ''}__${inputSuffix}`;
          generatedQ = generatedByTuple.get(tupleKey);

          // 4. Normalized tuple — also handles _cond suffix being dropped
          if (!generatedQ) {
            const normKey = `${remoteQ.category}__${remoteQ.optionType ?? ''}__${normalizeSuffix(inputSuffix)}`;
            generatedQ = generatedByNormTuple.get(normKey);
          }
        }

        if (generatedQ) {
          const mergedQuestion = mergeQuestionOptions(remoteQ, generatedQ);
          orderedQuestions.push(mergedQuestion);
          removeFromAllMaps(generatedQ);
        }
      });

      // Append new questions not present in remote flow — generatedByPath first (have
      // responsePaths), then any remaining questionsMap entries (no responsePath).
      generatedByPath.forEach((question) => {
        orderedQuestions.push(question);
        questionsMap.delete(question.questionId);
      });
      questionsMap.forEach((question) => {
        orderedQuestions.push(question);
      });

      return orderedQuestions.map((q, index) => {
        const questionWithCustomOptions = applyCustomOptionOrder(q);
        return {
          ...questionWithCustomOptions,
          step: index + 1,
        };
      });
    }

    // Priority 3: Default order (as generated) - for new setups
    return generatedQuestions.map((q, index) => {
      const questionWithCustomOptions = applyCustomOptionOrder(q);
      return {
        ...questionWithCustomOptions,
        step: index + 1,
      };
    });
  }, [generatedQuestions, questionOrder, remoteFlow, mergeQuestionOptions, applyCustomOptionOrder, normalizeSuffix]);

  // Check if user has made local changes (customizations or reordering)
  const hasUnsavedChanges = useMemo(() => {
    // Check if user has customized any question text
    const hasCustomizations = Object.keys(customizations).some(
      (key) => customizations[key]?.questionText || customizations[key]?.helperText
    );

    // Check if user has manually reordered questions
    const hasReordered = questionOrder.length > 0;

    // Check if user has manually reordered any options
    const hasOptionReordering = Object.keys(optionOrders).length > 0;

    return hasCustomizations || hasReordered || hasOptionReordering;
  }, [customizations, questionOrder, optionOrders]);

  // Initialize loading state
  useEffect(() => {
    if (isLoading && generatedQuestions.length > 0) {
      setIsLoading(false);
    }
  }, [isLoading, generatedQuestions]);

  const updateCustomization = useCallback(
    (questionId: string, customization: Partial<QuestionCustomization>) => {
      setCustomizations((prev) => ({
        ...prev,
        [questionId]: {
          ...prev[questionId],
          ...customization,
        },
      }));
    },
    []
  );

  const updateQuestionOrder = useCallback((newOrder: string[]) => {
    setQuestionOrder(newOrder);
  }, []);

  const updateOptionOrder = useCallback((questionId: string, newOrder: string[]) => {
    setOptionOrders((prev) => ({
      ...prev,
      [questionId]: newOrder,
    }));
  }, []);

  const resetCustomizations = useCallback(() => {
    setCustomizations({});
    setQuestionOrder([]);
    setOptionOrders({});
  }, []);

  const getQuestionWithCustomization = useCallback(
    (question: IFlowQuestion) => {
      const customization = customizations[question.questionId];
      const hasCustomText = customization?.questionText != null;
      return {
        ...question,
        question: customization?.questionText || question.question,
        helperText: customization?.helperText ?? question.helperText,
        // Set true when user has an active custom text this session, or preserve the
        // flag already loaded from the remote config. This gets saved to the backend so
        // the next load knows to prefer the user's text over auto-generated text.
        isCustomModified: hasCustomText ? true : (question.isCustomModified ?? false),
      };
    },
    [customizations]
  );

  return {
    questions,
    customizations,
    questionOrder,
    optionOrders,
    hasUnsavedChanges,
    isLoading,
    updateCustomization,
    updateQuestionOrder,
    updateOptionOrder,
    resetCustomizations,
    getQuestionWithCustomization,
  };
};
